/**
 * GOYA LISBOA — SEO metadata per route × language.
 *
 * Titles kept under ~60 chars, descriptions under ~155 chars
 * (Google truncates beyond those).
 *
 * `title` is appended with " · GOYA LISBOA" automatically by useSEO.
 * `getSEO(pathname, lang)` matches the most specific route.
 */

const SITE_NAME = "GOYA LISBOA";
const SITE_SUFFIX = ` · ${SITE_NAME}`;

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_lisboa-boutique-ui/artifacts/bam940fn_Outpaint_image_to_Greek_islands_202607111214.webp";

// key = route pattern (exact or startsWith), value = { en, pt, es }
const SEO = {
  "/": {
    en: {
      title: "Curated Lisbon Boutique — Mediterranean Fashion",
      description:
        "Discover GOYA LISBOA — a curated boutique of dresses, tops and total looks sourced from independent Mediterranean makers. Free EU shipping over €150.",
      keywords: "Lisbon boutique, Portuguese fashion, Mediterranean fashion, curated dresses, gingham dress, GOYA LISBOA",
    },
    pt: {
      title: "Boutique curada de Lisboa — Moda Mediterrânica",
      description:
        "Descobre a GOYA LISBOA — boutique curada de vestidos, tops e total looks de criadores independentes do Mediterrâneo. Envio grátis na UE acima de 150€.",
      keywords: "boutique Lisboa, moda portuguesa, vestidos, moda mediterrânica, roupa mulher, GOYA LISBOA",
    },
    es: {
      title: "Boutique curada de Lisboa — Moda mediterránea",
      description:
        "Descubre GOYA LISBOA — boutique curada de vestidos, tops y total looks de creadores independientes del Mediterráneo. Envío gratis en la UE desde 150€.",
      keywords: "boutique Lisboa, moda mediterránea, vestidos, moda portuguesa, ropa mujer, GOYA LISBOA",
    },
  },

  "/collection/all": {
    en: {
      title: "Shop All — SS26 Cyclades Edit",
      description: "The full GOYA LISBOA edit — dresses, tops, skirts, trousers and total looks. Sourced from Mediterranean makers, shipped from Lisbon.",
    },
    pt: {
      title: "Comprar tudo — Edição Cíclades SS26",
      description: "Toda a curadoria GOYA LISBOA — vestidos, tops, saias, calças e total looks. Selecionados no Mediterrâneo, enviados de Lisboa.",
    },
    es: {
      title: "Comprar todo — Edición Cíclades SS26",
      description: "Toda la selección GOYA LISBOA — vestidos, tops, faldas, pantalones y total looks. Curados en el Mediterráneo, enviados desde Lisboa.",
    },
  },

  "/collection/dresses": {
    en: {
      title: "Dresses — Gingham, Linen & Scallop",
      description: "Handpicked summer dresses in gingham, linen and scalloped cotton. Free EU shipping over €150. GOYA LISBOA.",
    },
    pt: {
      title: "Vestidos — Vichy, Linho e Festonado",
      description: "Vestidos de verão selecionados em vichy, linho e algodão festonado. Envio grátis na UE acima de 150€. GOYA LISBOA.",
    },
    es: {
      title: "Vestidos — Vichy, Lino y Festón",
      description: "Vestidos de verano seleccionados en vichy, lino y algodón festoneado. Envío gratis en la UE desde 150€. GOYA LISBOA.",
    },
  },

  "/collection/tops": {
    en: {
      title: "Tops & Blouses — Scalloped & Embroidered",
      description: "Scalloped, embroidered and hand-picked cotton tops for a bright Iberian summer. GOYA LISBOA.",
    },
    pt: {
      title: "Tops e Blusas — Festonadas e Bordadas",
      description: "Blusas festonadas, bordadas e em algodão selecionadas à mão para um verão ibérico luminoso. GOYA LISBOA.",
    },
    es: {
      title: "Tops y Blusas — Festón y Bordado",
      description: "Blusas festoneadas, bordadas y en algodón seleccionadas para un verano ibérico luminoso. GOYA LISBOA.",
    },
  },

  "/collection/skirts": {
    en: {
      title: "Skirts — Midi, Stripe & Flare",
      description: "Curated summer skirts — striped midis, flared cottons and playful prints. GOYA LISBOA, Lisbon.",
    },
    pt: {
      title: "Saias — Midi, Riscas e Godé",
      description: "Saias de verão selecionadas — midi de riscas, algodões godé e padrões alegres. GOYA LISBOA, Lisboa.",
    },
    es: {
      title: "Faldas — Midi, Rayas y Vuelo",
      description: "Faldas de verano curadas — midi a rayas, algodones con vuelo y estampados alegres. GOYA LISBOA, Lisboa.",
    },
  },

  "/collection/trousers": {
    en: {
      title: "Trousers — Wide-Leg & Linen",
      description: "Breathable linen and wide-leg cotton trousers — the perfect summer weight. GOYA LISBOA.",
    },
    pt: {
      title: "Calças — Perna Larga e Linho",
      description: "Calças de linho respirável e perna larga em algodão — o peso perfeito para o verão. GOYA LISBOA.",
    },
    es: {
      title: "Pantalones — Pierna Ancha y Lino",
      description: "Pantalones de lino transpirable y pierna ancha en algodón — el peso ideal para el verano. GOYA LISBOA.",
    },
  },

  "/collection/jackets": {
    en: {
      title: "Jackets — Lightweight Summer Layers",
      description: "Lightweight summer layers — cotton jackets, linen blazers and boxy silhouettes. GOYA LISBOA.",
    },
    pt: {
      title: "Casacos — Camadas Leves de Verão",
      description: "Camadas leves para o verão — casacos de algodão, blazers de linho e silhuetas boxy. GOYA LISBOA.",
    },
    es: {
      title: "Chaquetas — Capas Ligeras de Verano",
      description: "Capas ligeras para el verano — chaquetas de algodón, blazers de lino y siluetas boxy. GOYA LISBOA.",
    },
  },

  "/collection/total-look": {
    en: {
      title: "Total Look — Head-to-Toe Edits",
      description: "Complete head-to-toe looks — save 10% when you shop the bundle. GOYA LISBOA.",
    },
    pt: {
      title: "Total Look — Conjuntos Completos",
      description: "Conjuntos completos da cabeça aos pés — poupa 10% ao comprar o conjunto. GOYA LISBOA.",
    },
    es: {
      title: "Total Look — Conjuntos Completos",
      description: "Conjuntos completos de la cabeza a los pies — ahorra un 10% al comprar el pack. GOYA LISBOA.",
    },
  },

  "/journal": {
    en: {
      title: "Journal — Iberian Style Notes",
      description: "The GOYA LISBOA journal — style notes, editorial shoots and postcards from the Mediterranean.",
    },
    pt: {
      title: "Diário — Notas de Estilo Ibérico",
      description: "O diário GOYA LISBOA — notas de estilo, editoriais e postais do Mediterrâneo.",
    },
    es: {
      title: "Diario — Notas de Estilo Ibérico",
      description: "El diario GOYA LISBOA — notas de estilo, editoriales y postales del Mediterráneo.",
    },
  },

  "/about": {
    en: {
      title: "About us — Meet the founders",
      description: "GOYA LISBOA is run by twin sisters who just turned 30 — a lifelong obsession with fashion, curated for you.",
    },
    pt: {
      title: "Sobre nós — Conhece as fundadoras",
      description: "A GOYA LISBOA é gerida por gémeas que acabaram de fazer 30 anos — uma paixão de uma vida pela moda, curada para ti.",
    },
    es: {
      title: "Sobre nosotras — Conoce a las fundadoras",
      description: "GOYA LISBOA está gestionada por hermanas gemelas que acaban de cumplir 30 — una pasión de toda la vida por la moda, curada para ti.",
    },
  },

  "/account": {
    en: { title: "Account", description: "Your GOYA LISBOA account — orders and reviews.", noindex: true },
    pt: { title: "Conta", description: "A tua conta GOYA LISBOA — encomendas e avaliações.", noindex: true },
    es: { title: "Cuenta", description: "Tu cuenta GOYA LISBOA — pedidos y reseñas.", noindex: true },
  },

  "/checkout/success": {
    en: { title: "Order Confirmed", description: "Thank you — your order is confirmed.", noindex: true },
    pt: { title: "Encomenda Confirmada", description: "Obrigada — a tua encomenda está confirmada.", noindex: true },
    es: { title: "Pedido Confirmado", description: "Gracias — tu pedido está confirmado.", noindex: true },
  },
};

// Prefix match for /help/* and /product/* fallbacks
const PREFIX_SEO = {
  "/help/": {
    en: { title: "Help & FAQ", description: "Shipping, returns and contact information for GOYA LISBOA." },
    pt: { title: "Ajuda e FAQ", description: "Informação sobre envio, devoluções e contacto GOYA LISBOA." },
    es: { title: "Ayuda y FAQ", description: "Información sobre envío, devoluciones y contacto GOYA LISBOA." },
  },
  "/product/": {
    en: { title: "Product", description: "Discover the piece — a hand-curated find from GOYA LISBOA." },
    pt: { title: "Produto", description: "Descobre a peça — uma seleção curada pela GOYA LISBOA." },
    es: { title: "Producto", description: "Descubre la pieza — una selección curada por GOYA LISBOA." },
  },
};

export const DEFAULT_OG_IMAGE = HERO_IMAGE;
export { SITE_NAME, SITE_SUFFIX };

export function getSEO(pathname, lang = "en") {
  const l = ["en", "pt", "es"].includes(lang) ? lang : "en";
  if (SEO[pathname]?.[l]) return SEO[pathname][l];
  for (const prefix of Object.keys(PREFIX_SEO)) {
    if (pathname.startsWith(prefix)) return PREFIX_SEO[prefix][l];
  }
  return SEO["/"][l];
}
